import { api } from '../api.js';
import { esc } from '../../../../src/shared/escape-html.js';
import { confirmAdminRoleChange } from '../admin-role-confirmation.js';
import { runSensitiveAdminMutation } from '../admin-security.js';

function fmtDate(iso) {
  if (!iso) return '–';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function fmtDuration(seconds) {
  if (!seconds) return '–';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

const PROVIDER_LABELS = { credential: 'password', discord: 'discord', google: 'google', twitch: 'twitch' };

function providerBadges(providers) {
  return (providers || [])
    .map(p => `<span class="admin-user-provider admin-user-provider-${esc(p)}">${esc(PROVIDER_LABELS[p] || p)}</span>`)
    .join('') || '–';
}

function roleBadges(roles) {
  return (roles || [])
    .map(r => `<span class="admin-user-role admin-user-role-${esc(r.role)}">${esc(r.role)}</span>`)
    .join('') || '<span class="admin-user-role-none">none</span>';
}

function statusMsg(el, text, success) {
  if (!el) return;
  el.textContent = text;
  el.style.color = success ? 'var(--accent)' : '#f06060';
  setTimeout(() => { el.textContent = ''; }, 3000);
}

async function loadUserDetail(id) {
  const content = document.getElementById('admin-user-detail-content');
  if (!content) return;
  content.innerHTML = '<div class="admin-status">Loading...</div>';

  const [userRes, tokensRes] = await Promise.allSettled([
    api.get(`/api/admin/users/${encodeURIComponent(id)}`),
    api.get(`/api/store/admin/user/${encodeURIComponent(id)}`),
  ]);
  if (userRes.status === 'rejected') {
    content.innerHTML = `<div class="admin-status">Failed to load user: ${esc(userRes.reason?.message || 'unknown')}</div>`;
    return;
  }
  const data = userRes.value;
  // tokensData is { balance, lifetime_earned, inventory, equipped } on success,
  // or null when the store module is disabled / endpoint missing.
  const tokensData = tokensRes.status === 'fulfilled' ? tokensRes.value : null;

  const u = data.user;
  const s = data.settings;
  const sanctions = data.sanctions;
  const stats = data.stats;
  const retiredUnlocks = Number(stats.achievement_retired_unlocks) || 0;

  const verifiedBadge = u.email_verified
    ? '<span class="admin-user-role admin-user-role-admin" style="font-size:9px">verified</span>'
    : '<span class="admin-user-unverified">unverified</span>';

  const avatarHtml = u.image
    ? `<img class="user-detail-avatar" src="/api/users/${esc(u.username)}/avatar" alt="avatar" onerror="this.style.display='none'">`
    : '';

  const sanctionHtml = sanctions
    ? `<div class="contact-detail-meta">
        ${sanctions.ban_until ? `<div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Banned until</span><span>${esc(fmtDate(sanctions.ban_until))}</span></div>` : ''}
        ${sanctions.mute_until ? `<div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Muted until</span><span>${esc(fmtDate(sanctions.mute_until))}</span></div>` : ''}
        ${sanctions.reason ? `<div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Reason</span><span>${esc(sanctions.reason)}</span></div>` : ''}
        <div style="margin-top:8px"><button class="btn btn-mode" id="ud-lift-sanction">Lift sanctions</button> <span id="ud-sanction-status" class="admin-status" style="margin:0"></span></div>
      </div>`
    : '<span class="admin-user-role-none">No active sanctions</span>';

  const customStatusRow = s?.custom_status
    ? `<div class="user-detail-edit-row">
        <span style="color:var(--text-dim)">Custom status:</span>
        <span>"${esc(s.custom_status)}"</span>
        <button class="btn btn-mode" id="ud-clear-status">Clear</button>
        <span id="ud-status-clear-msg" class="admin-status" style="margin:0"></span>
      </div>`
    : '';

  const sessionsHtml = data.recent_sessions.length
    ? `<table class="admin-users-table" style="font-size:11px">
        <thead><tr><th>Date</th><th>Game</th><th>System</th><th>Duration</th><th>Mode</th></tr></thead>
        <tbody>${data.recent_sessions.map(sess => `
          <tr>
            <td style="white-space:nowrap">${esc(fmtDate(sess.started_at))}</td>
            <td>${esc(sess.game_title || '–')}</td>
            <td>${esc(sess.system_id)}</td>
            <td>${fmtDuration(sess.duration_seconds)}</td>
            <td>${esc(sess.net_mode)}</td>
          </tr>`).join('')}
        </tbody>
      </table>`
    : '<span class="admin-user-role-none">No play sessions</span>';

  const tokensHtml = tokensData
    ? `
      <div class="contact-detail-meta">
        <div class="contact-detail-meta-row">
          <span class="contact-detail-meta-label">Current balance</span>
          <span style="font-size:18px;font-weight:bold">${(tokensData.balance ?? 0).toLocaleString()}</span>
        </div>
        <div class="contact-detail-meta-row">
          <span class="contact-detail-meta-label">Lifetime earned</span>
          <span>${(tokensData.lifetime_earned ?? 0).toLocaleString()}</span>
        </div>
      </div>
      <div class="user-detail-edit-row" style="margin-top:12px">
        <span class="contact-detail-meta-label">Grant</span>
        <input type="number" min="1" class="settings-input" id="ud-grant-tokens-amount" placeholder="amount" style="max-width:100px">
        <input type="text" class="settings-input" id="ud-grant-tokens-reason" value="admin grant" placeholder="reason">
        <button class="btn btn-mode btn-primary" id="ud-grant-tokens">Grant</button>
        <span id="ud-grant-tokens-status" class="admin-status" style="margin:0"></span>
      </div>
    `
    : '<span class="admin-user-role-none">Store module disabled</span>';

  const settingsHtml = s ? `
    <div class="contact-detail-meta">
      <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Room public</span><span>${s.room_public ? 'Yes' : 'No'}</span></div>
      <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Timezone</span><span>${esc(s.timezone || '–')}</span></div>
      <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Theme</span><span>${esc(s.theme || 'default')}</span></div>
      <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Chat</span><span>${s.chat_enabled ? 'On' : 'Off'}</span></div>
      <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Voice chat</span><span>${s.voice_chat_enabled ? 'On' : 'Off'}</span></div>
      <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Friends list</span><span>${esc(s.friends_list_visibility || '–')}</span></div>
      <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Friend requests</span><span>${esc(s.friend_request_policy || '–')}</span></div>
      <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Email friend req</span><span>${s.email_friend_requests ? 'On' : 'Off'}</span></div>
      <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Email invites</span><span>${s.email_play_invites ? 'On' : 'Off'}</span></div>
      <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Pending digest</span><span>${s.email_pending_friend_digest ? 'On' : 'Off'}</span></div>
    </div>
  ` : '<span class="admin-user-role-none">No settings saved</span>';

  content.innerHTML = `
    <div class="card admin-card">
      <div class="card-label">PROFILE</div>
      <div style="display:flex;gap:20px;align-items:flex-start">
        ${avatarHtml}
        <div class="contact-detail-meta" style="flex:1">
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Username</span><span>${esc(u.username)}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Name</span><span>${u.name ? esc(u.name) : '<span class="admin-user-role-none">–</span>'}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Email</span><span>${esc(u.email)} ${verifiedBadge}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">ID</span><span style="font-family:var(--font-mono);font-size:11px;color:var(--text-dim)">${esc(u.id)}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Providers</span><span>${providerBadges(data.providers)}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Roles</span><span>${roleBadges(data.roles)}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Joined</span><span>${esc(fmtDate(u.created_at))}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Updated</span><span>${esc(fmtDate(u.updated_at))}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Date of birth</span><span>${u.date_of_birth ? esc(String(u.date_of_birth).slice(0, 10)) : '–'}</span></div>
          ${u.tos_accepted_at ? `<div class="contact-detail-meta-row"><span class="contact-detail-meta-label">TOS accepted</span><span>${esc(fmtDate(u.tos_accepted_at))}</span></div>` : ''}
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Legal version</span><span>${u.legal_version ? esc(u.legal_version) : '–'}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Age-rule status</span><span>${u.age_requirement_grandfathered ? 'Grandfathered' : '13+ required'}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Test account</span>
            <label class="admin-toggle"><input type="checkbox" id="ud-is-test" ${u.is_test ? 'checked' : ''}><span class="admin-toggle-slider"></span></label>
          </div>
        </div>
      </div>
    </div>

    <div class="card admin-card">
      <div class="card-label">EDIT USER</div>
      <div style="display:flex;flex-direction:column;gap:12px">
        <div class="user-detail-edit-row">
          <span class="contact-detail-meta-label">Name</span>
          <input type="text" class="settings-input" id="ud-name" value="${esc(u.name || '')}" placeholder="Full name">
          <button class="btn btn-mode btn-primary" id="ud-save-name">Save</button>
          <span id="ud-name-status" class="admin-status" style="margin:0"></span>
        </div>
        <div class="user-detail-edit-row">
          <span class="contact-detail-meta-label">Display Name</span>
          <input type="text" class="settings-input" id="ud-display-username" value="${esc(u.display_username || '')}">
          <button class="btn btn-mode btn-primary" id="ud-save-display-username">Save</button>
          <span id="ud-display-username-status" class="admin-status" style="margin:0"></span>
        </div>
        <div class="user-detail-edit-row">
          <span class="contact-detail-meta-label">Username</span>
          <input type="text" class="settings-input" id="ud-username" value="${esc(u.username)}">
          <button class="btn btn-mode btn-primary" id="ud-save-username">Save</button>
          <span id="ud-username-status" class="admin-status" style="margin:0"></span>
        </div>
        <div class="user-detail-edit-row">
          <span class="contact-detail-meta-label">Email</span>
          <input type="text" class="settings-input" id="ud-email" value="${esc(u.email)}">
          <button class="btn btn-mode btn-primary" id="ud-save-email">Save</button>
          <span id="ud-email-status" class="admin-status" style="margin:0"></span>
        </div>
        <div class="user-detail-edit-row">
          <span class="contact-detail-meta-label">Email verified</span>
          <label class="admin-toggle"><input type="checkbox" id="ud-email-verified" ${u.email_verified ? 'checked' : ''}><span class="admin-toggle-slider"></span></label>
          <span id="ud-verified-status" class="admin-status" style="margin:0"></span>
        </div>
        ${customStatusRow}
      </div>
    </div>

    <div class="card admin-card">
      <div class="card-label">ROLES</div>
      <div style="margin-bottom:8px">${roleBadges(data.roles)}</div>
      <div class="user-detail-edit-row">
        <select class="settings-input settings-select" id="ud-role-select" style="max-width:140px">
          <option value="admin">Admin</option>
          <option value="moderator">Moderator</option>
          <option value="library_full_access">Library Access</option>
        </select>
        <button class="btn btn-mode btn-primary" id="ud-grant-role">Grant</button>
        ${data.roles.length ? `<button class="btn btn-mode" id="ud-revoke-role">Revoke selected</button>` : ''}
        <span id="ud-role-status" class="admin-status" style="margin:0"></span>
      </div>
    </div>

    <div class="card admin-card">
      <div class="card-label">SANCTIONS</div>
      ${sanctionHtml}
    </div>

    <div class="card admin-card">
      <div class="card-label">PLAY STATS</div>
      <div class="user-detail-stats">
        <div class="user-detail-stat"><div class="user-detail-stat-val">${stats.session_count.toLocaleString()}</div><div class="user-detail-stat-label">Sessions</div></div>
        <div class="user-detail-stat"><div class="user-detail-stat-val">${fmtDuration(stats.play_time_seconds)}</div><div class="user-detail-stat-label">Play time</div></div>
        <div class="user-detail-stat"><div class="user-detail-stat-val">${stats.games_played.toLocaleString()}</div><div class="user-detail-stat-label">Games</div></div>
        <div class="user-detail-stat"><div class="user-detail-stat-val">${stats.achievement_points.toLocaleString()}</div><div class="user-detail-stat-label">Points</div></div>
        <div class="user-detail-stat"><div class="user-detail-stat-val">${stats.achievement_unlocks.toLocaleString()}</div><div class="user-detail-stat-label">${retiredUnlocks > 0 ? `Unlocks (${retiredUnlocks.toLocaleString()} retired)` : 'Unlocks'}</div></div>
      </div>
    </div>

    <div class="card admin-card">
      <div class="card-label">TOKENS</div>
      ${tokensHtml}
    </div>

    <div class="card admin-card">
      <div class="card-label">SETTINGS</div>
      ${settingsHtml}
    </div>

    <div class="card admin-card">
      <div class="card-label">RECENT SESSIONS</div>
      ${sessionsHtml}
    </div>
  `;

  // ── Event bindings ──────────────────────────────────────

  const patchUser = async (body, statusEl, actionLabel) => {
    try {
      const completed = await runSensitiveAdminMutation({
        actionLabel,
        mutate: (reason) => api.patch(
          `/api/admin/users/${encodeURIComponent(id)}`,
          { ...body, reason },
        ),
      });
      if (!completed) return false;
      statusMsg(statusEl, 'Saved', true);
      return true;
    } catch (err) {
      statusMsg(statusEl, err.body || err.message, false);
      return false;
    }
  };

  document.getElementById('ud-is-test')?.addEventListener('change', async (e) => {
    const value = e.target.checked;
    if (!await patchUser({ is_test: value }, null, `set ${u.username}'s test-account status`)) {
      e.target.checked = !value;
    }
  });

  document.getElementById('ud-save-name')?.addEventListener('click', () => {
    const val = document.getElementById('ud-name').value.trim();
    patchUser({ name: val }, document.getElementById('ud-name-status'), `change ${u.username}'s name`);
  });

  document.getElementById('ud-save-display-username')?.addEventListener('click', () => {
    const val = document.getElementById('ud-display-username').value.trim();
    patchUser({ displayUsername: val }, document.getElementById('ud-display-username-status'), `change ${u.username}'s display username`);
  });

  document.getElementById('ud-save-username')?.addEventListener('click', () => {
    const val = document.getElementById('ud-username').value.trim();
    patchUser({ username: val }, document.getElementById('ud-username-status'), `change ${u.username}'s username`);
  });

  document.getElementById('ud-save-email')?.addEventListener('click', () => {
    const val = document.getElementById('ud-email').value.trim();
    patchUser({ email: val }, document.getElementById('ud-email-status'), `change ${u.username}'s email address`);
  });

  document.getElementById('ud-email-verified')?.addEventListener('change', async (e) => {
    const value = e.target.checked;
    if (!await patchUser({ emailVerified: value }, document.getElementById('ud-verified-status'), `change ${u.username}'s email verification status`)) {
      e.target.checked = !value;
    }
  });

  document.getElementById('ud-clear-status')?.addEventListener('click', () => {
    patchUser({ custom_status: null }, document.getElementById('ud-status-clear-msg'), `clear ${u.username}'s custom status`);
  });

  document.getElementById('ud-grant-role')?.addEventListener('click', async () => {
    const role = document.getElementById('ud-role-select').value;
    const st = document.getElementById('ud-role-status');
    if (!confirmAdminRoleChange({ operation: 'grant', role, username: u.username })) return;
    try {
      const completed = await runSensitiveAdminMutation({
        actionLabel: `grant ${role} access to ${u.username}`,
        mutate: (reason) => api.post('/api/admin/roles', { user_id: id, role, reason }),
      });
      if (!completed) return;
      statusMsg(st, `Granted ${role}`, true);
      loadUserDetail(id);
    } catch (err) {
      statusMsg(st, err.body || err.message, false);
    }
  });

  document.getElementById('ud-revoke-role')?.addEventListener('click', async () => {
    const role = document.getElementById('ud-role-select').value;
    const st = document.getElementById('ud-role-status');
    if (!confirmAdminRoleChange({ operation: 'revoke', role, username: u.username })) return;
    try {
      const completed = await runSensitiveAdminMutation({
        actionLabel: `revoke ${role} access from ${u.username}`,
        mutate: (reason) => api.delete('/api/admin/roles', { user_id: id, role, reason }),
      });
      if (!completed) return;
      statusMsg(st, `Revoked ${role}`, true);
      loadUserDetail(id);
    } catch (err) {
      statusMsg(st, err.body || err.message, false);
    }
  });

  document.getElementById('ud-grant-tokens')?.addEventListener('click', async () => {
    const amountEl = document.getElementById('ud-grant-tokens-amount');
    const reasonEl = document.getElementById('ud-grant-tokens-reason');
    const st = document.getElementById('ud-grant-tokens-status');
    const amount = parseInt(amountEl.value, 10);
    const reason = reasonEl.value.trim();
    if (!Number.isFinite(amount) || amount <= 0) {
      statusMsg(st, 'Amount must be a positive integer', false);
      return;
    }
    if (!reason) {
      statusMsg(st, 'Reason required', false);
      return;
    }
    const btn = document.getElementById('ud-grant-tokens');
    btn.disabled = true;
    try {
      await api.post('/api/store/admin/grant', { userId: id, amount, reason });
      statusMsg(st, `Granted ${amount.toLocaleString()} tokens`, true);
      amountEl.value = '';
      loadUserDetail(id);
    } catch (err) {
      btn.disabled = false;
      statusMsg(st, err.body || err.message, false);
    }
  });

  document.getElementById('ud-lift-sanction')?.addEventListener('click', async () => {
    const st = document.getElementById('ud-sanction-status');
    try {
      await api.delete(`/api/admin/moderation/sanctions/${encodeURIComponent(id)}`);
      statusMsg(st, 'Lifted', true);
      loadUserDetail(id);
    } catch (err) {
      statusMsg(st, err.body || err.message, false);
    }
  });
}

export function renderDetail(params) {
  loadUserDetail(params.id);
}
